// The count-anchored summary shown ABOVE an insight — "M satisfy this condition (x% of N)" (M = the rows
// the current condition would keep). When the row count is unknown it falls back to "About x%", and when
// the condition value is cleared it shows a placeholder instead of a number.

import { formatCount, formatPercent } from './lib/format';

export function InsightSummary({
  matchCount,
  total,
  sharePct,
  unset = false,
  unsetLabel = 'No value set',
  estimated = false,
  totalEstimated = false,
  bound,
}: {
  /** Rows the current condition matches, already operator-adjusted by the caller. ABSENT ⇒ unknown —
   *  the line goes quiet rather than printing a number nobody measured. */
  matchCount?: number;
  total?: number;
  /** A handle-responsive share (0–100) shown as "About X%" when an absolute count can't be computed
   *  (e.g. the row count is unknown). An available `matchCount` always takes precedence over it. */
  sharePct?: number;
  unset?: boolean; // the value is cleared — show the placeholder instead of a count
  unsetLabel?: string;
  /** The count is derived (quantile bands), not measured — say so instead of implying a measurement. */
  estimated?: boolean;
  /**
   * The TOTAL is a catalog estimate rather than a real count, so the denominator — and the share drawn
   * against it — carry the qualifier instead of the match count. Separate from {@link estimated}
   * because the two halves of this line go soft for independent reasons: a positive pill selection has
   * a measured count sitting over an estimated denominator, and prefixing the count with "About" there
   * would mark the wrong half.
   */
  totalEstimated?: boolean;
  /**
   * The figure is a BOUND, not an estimate of the answer: `upper` ⇒ "Up to N", `lower` ⇒ "At least N".
   * Set for the single-value operators, where binned data can only bracket the count (see
   * `lib/bucket-bound.ts`). It outranks `estimated` — "About" would imply the number is centred on the
   * truth, and the whole point of a bound is that the truth sits to one side of it.
   */
  bound?: 'upper' | 'lower';
}) {
  const known = matchCount !== undefined;
  // A count ABOVE an estimated total is evidence the TOTAL is wrong, not the count: `reltuples` routinely
  // under-reports after a bulk load. Clamping there would print the estimate in bold as though it were the
  // measurement, at a fabricated 100%. So the clamp applies only to a total we trust, and when a soft one
  // is exceeded the measured count stands and the parenthetical goes quiet — a share against a denominator
  // known to be too small is not a share.
  const totalExceeded = known && total !== undefined && matchCount > total;
  const denominatorUsable = total !== undefined && !(totalEstimated && totalExceeded);
  const matched = !known
    ? undefined
    : denominatorUsable && total !== undefined
      ? Math.min(total, Math.max(0, matchCount))
      : Math.max(0, matchCount);
  const share = matched !== undefined && denominatorUsable && total && total > 0 ? (matched / total) * 100 : undefined;
  // The count leads; the total sits with the % in a trailing parenthetical, kept away from the count so the
  // two figures never read as one run of digits. `formatPercent`, not a bare round — this is the panel's
  // most-shown percentage, and the guards that stop it reading as a flat `100%` or a confident `0%` belong
  // here at least as much as on the share readout below.
  const context =
    denominatorUsable && total !== undefined && share !== undefined
      ? ` (${formatPercent(share)} of ${totalEstimated ? 'about ' : ''}${formatCount(total)})`
      : '';
  // "About >99%" reads as two hedges stacked. A formatted share already carrying a comparator IS the
  // hedge, so the word is redundant in front of it.
  const shareText = sharePct !== undefined ? formatPercent(sharePct) : undefined;
  const comparatorLed = shareText !== undefined && (shareText.startsWith('<') || shareText.startsWith('>'));
  const qualifier =
    bound === 'upper' ? 'Up to ' : bound === 'lower' ? 'At least ' : estimated && !comparatorLed ? 'About ' : '';
  return (
    <p className="text-xs text-muted-foreground">
      {unset ? (
        unsetLabel
      ) : matched !== undefined ? (
        <>
          {qualifier}
          <span className="font-semibold text-foreground">{formatCount(matched)}</span> satisfy this condition
          {context}
        </>
      ) : shareText !== undefined ? (
        <>
          {qualifier}
          <span className="font-semibold text-foreground">{shareText}</span> satisfy this condition
        </>
      ) : (
        'Match count not available'
      )}
    </p>
  );
}
